import axios from 'axios';
import api from './api';

const BASE = '/rrhh-inventario';

// Cliente sin token ni redirección a /login: lo usa el formulario público de
// solicitudes de colaboradores (no tienen cuenta en Argus).
const publicApi = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'https://apifractial.homedeliverype.com/api/v1',
    headers: { 'Content-Type': 'application/json' },
});

const descargarBlob = (data, nombre) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', nombre);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const rrhhInventarioService = {
    // ─── Empleados ────────────────────────────────────────────────────────────
    listarEmpleados: async (q = null, soloActivos = true) => {
        const params = { solo_activos: soloActivos };
        if (q) params.q = q;
        const response = await api.get(`${BASE}/empleados`, { params });
        return response.data;
    },
    crearEmpleado: async (data) => {
        const response = await api.post(`${BASE}/empleados`, data);
        return response.data;
    },
    actualizarEmpleado: async (id, data) => {
        const response = await api.put(`${BASE}/empleados/${id}`, data);
        return response.data;
    },
    eliminarEmpleado: async (id) => {
        const response = await api.delete(`${BASE}/empleados/${id}`);
        return response.data;
    },
    cargarExcelEmpleados: async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post(`${BASE}/empleados/upload-excel`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
    descargarPlantillaEmpleados: async () => {
        const response = await api.get(`${BASE}/empleados/plantilla/excel`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Plantilla_RRHH_Empleados.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },

    // ─── Catálogo (ítems) ─────────────────────────────────────────────────────
    listarItems: async (q = null, soloActivos = true) => {
        const params = { solo_activos: soloActivos };
        if (q) params.q = q;
        const response = await api.get(`${BASE}/items`, { params });
        return response.data;
    },
    crearItem: async (data) => {
        const response = await api.post(`${BASE}/items`, data);
        return response.data;
    },
    actualizarItem: async (id, data) => {
        const response = await api.put(`${BASE}/items/${id}`, data);
        return response.data;
    },
    eliminarItem: async (id) => {
        const response = await api.delete(`${BASE}/items/${id}`);
        return response.data;
    },

    // IA: analiza la foto y devuelve una sugerencia (NO guarda el producto)
    analizarFoto: async (foto) => {
        const formData = new FormData();
        formData.append('foto', foto);
        const response = await api.post(`${BASE}/items/analizar-foto`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    cargarExcel: async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post(`${BASE}/items/upload-excel`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    exportarInventarioExcel: async (q = null) => {
        const response = await api.get(`${BASE}/items/exportar/excel`, {
            params: q ? { q } : {},
            responseType: 'blob',
        });
        const hoy = new Date().toISOString().slice(0, 10);
        descargarBlob(response.data, `Inventario_RRHH_${hoy}.xlsx`);
    },

    descargarPlantilla: async () => {
        const response = await api.get(`${BASE}/items/plantilla/excel`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Plantilla_RRHH_Inventario.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },

    // ─── Movimientos ──────────────────────────────────────────────────────────
    registrarMovimiento: async (data) => {
        const response = await api.post(`${BASE}/movimientos`, data);
        return response.data;
    },
    listarMovimientos: async (filtros = {}) => {
        const response = await api.get(`${BASE}/movimientos`, { params: filtros });
        return response.data;
    },

    eliminarMovimiento: async (movimientoId) => {
        const response = await api.delete(`${BASE}/movimientos/${movimientoId}`);
        return response.data;
    },
    marcarRenovado: async (movimientoId) => {
        const response = await api.post(`${BASE}/movimientos/${movimientoId}/renovado`);
        return response.data;
    },

    // ─── Entregas + Formato EPP (SUNAFIL) ─────────────────────────────────────
    registrarEntrega: async (data) => {
        const response = await api.post(`${BASE}/entregas`, data);
        return response.data;
    },
    descargarFormatoEpp: async (entregaCodigo) => {
        const response = await api.get(`${BASE}/entregas/${entregaCodigo}/formato-epp`, { responseType: 'blob' });
        descargarBlob(response.data, `Formato_EPP_${entregaCodigo}.pdf`);
    },
    subirFormatoFirmado: async (entregaCodigo, archivo) => {
        const formData = new FormData();
        formData.append('archivo', archivo);
        const response = await api.post(`${BASE}/entregas/${entregaCodigo}/formato-firmado`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    // ─── Caducidad de lo entregado ────────────────────────────────────────────
    listarCaducidades: async (dias = 30) => {
        const response = await api.get(`${BASE}/caducidades`, { params: { dias } });
        return response.data;
    },

    // ─── Solicitudes de colaboradores (atención) ──────────────────────────────
    listarSolicitudes: async (filtros = {}) => {
        const response = await api.get(`${BASE}/solicitudes`, { params: filtros });
        return response.data;
    },
    cambiarEstadoSolicitud: async (id, data) => {
        const response = await api.put(`${BASE}/solicitudes/${id}/estado`, data);
        return response.data;
    },
    atenderSolicitudConEntrega: async (id, data) => {
        const response = await api.post(`${BASE}/solicitudes/${id}/atender-entrega`, data);
        return response.data;
    },

    resumen: async () => {
        const response = await api.get(`${BASE}/resumen`);
        return response.data;
    },
};

// ─── Formulario público (sin login) ─────────────────────────────────────────
export const rrhhPublicoService = {
    catalogo: async () => {
        const response = await publicApi.get('/rrhh-publico/catalogo');
        return response.data;
    },
    tipos: async () => {
        const response = await publicApi.get('/rrhh-publico/tipos');
        return response.data;
    },
    enviarSolicitud: async (data) => {
        const response = await publicApi.post('/rrhh-publico/solicitudes', data);
        return response.data;
    },
    consultarSolicitud: async (codigo, documento) => {
        const response = await publicApi.get(`/rrhh-publico/solicitudes/${encodeURIComponent(codigo)}`, { params: { documento } });
        return response.data;
    },
};
